import { motion } from "framer-motion";
import { Reveal } from "../lib/motion";
import { useSeo } from "../lib/seo";
import { DecisionPath, RhythmBar } from "../components/p1/Graphics";
import { Eyebrow, SectionHead, PrimaryCTA, TextLink, Section, scrollToId } from "../components/p1/Primitives";
import { HOME } from "../lib/phase1";

const fade = (delay) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.7, ease: [0.16, 1, 0.3, 1] },
});

const Hero = () => (
  <section className="relative overflow-hidden border-b border-border bg-background pt-[72px] rule-grid" data-testid="hero">
    <div className="container-x relative grid gap-12 pb-24 pt-20 lg:grid-cols-12 lg:pb-32 lg:pt-28">
      <div className="lg:col-span-7">
        <motion.div {...fade(0.05)}><Eyebrow testid="hero-eyebrow">{HOME.hero.eyebrow}</Eyebrow></motion.div>
        <motion.h1
          {...fade(0.15)}
          data-testid="hero-title"
          className="mt-7 text-balance text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl"
        >
          Better technology decisions.<br />
          <span className="display font-medium italic text-[#0133BA] dark:text-primary">Accountable delivery.</span>
        </motion.h1>
        <motion.p {...fade(0.3)} data-testid="hero-copy" className="mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground">
          {HOME.hero.copy}
        </motion.p>
        <motion.div {...fade(0.45)} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
          <PrimaryCTA to="/contact" testid="hero-cta-primary">{HOME.hero.primary}</PrimaryCTA>
          <TextLink href="#method" onClick={scrollToId("method")} testid="hero-cta-secondary">{HOME.hero.secondary}</TextLink>
        </motion.div>
        <motion.p {...fade(0.6)} data-testid="hero-trust" className="label mt-14 text-muted-foreground">
          {HOME.hero.trust}
        </motion.p>
      </div>
      <div className="hidden lg:col-span-5 lg:flex lg:items-center lg:justify-end">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4, duration: 0.8 }} className="w-full max-w-[420px] text-foreground">
          <DecisionPath className="w-full" />
        </motion.div>
      </div>
    </div>
  </section>
);

const Tension = () => (
  <Section testid="tension">
    <div className="grid gap-12 lg:grid-cols-12">
      <SectionHead title={HOME.tension.h2} className="lg:col-span-7" testid="tension-title" />
      <Reveal delay={0.1} className="lg:col-span-5 lg:pt-2">
        {HOME.tension.body.map((p, i) => (
          <p key={i} className={`leading-relaxed ${i ? "mt-5 font-medium text-foreground" : "text-muted-foreground"}`}>{p}</p>
        ))}
      </Reveal>
    </div>
  </Section>
);

const Fit = () => (
  <Section tint testid="fit">
    <SectionHead title={HOME.fit.h2} testid="fit-title" />
    <Reveal delay={0.05} className="mt-6 max-w-3xl">
      <p className="leading-relaxed text-muted-foreground">{HOME.fit.body}</p>
    </Reveal>
    <ol className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-border bg-border md:grid-cols-2 lg:grid-cols-5" data-testid="fit-list">
      {HOME.fit.items.map((t, i) => (
        <Reveal key={i} as="li" delay={i * 0.06} className="flex flex-col bg-background p-7">
          <span className="label text-primary">0{i + 1}</span>
          <p className="mt-5 text-[15px] leading-relaxed text-foreground">{t}</p>
        </Reveal>
      ))}
    </ol>
  </Section>
);

const RouteCard = ({ r, i, testid }) => (
  <Reveal delay={i * 0.1} className="flex flex-col rounded-2xl border border-white/12 bg-white/[0.04] p-8 backdrop-blur-sm transition-colors duration-300 hover:border-white/30 md:p-10" data-testid={testid}>
    <span className="label text-white/55">{r.label}</span>
    <h3 className="mt-6 text-2xl font-semibold leading-tight text-white">{r.title}</h3>
    <p className="mt-5 leading-relaxed text-white/70">{r.body}</p>
    <div className="mt-auto pt-10">
      <PrimaryCTA to="/contact" light testid={`${testid}-cta`}>{r.cta}</PrimaryCTA>
    </div>
  </Reveal>
);

const TwoRoutes = () => (
  <Section dark testid="routes" className="overflow-hidden rule-grid-light">
    <SectionHead light title={HOME.routes.h2} testid="routes-title" />
    <div className="mt-14 grid gap-5 lg:grid-cols-2">
      {HOME.routes.items.map((r, i) => <RouteCard key={r.label} r={r} i={i} testid={`route-${r.label.toLowerCase()}`} />)}
    </div>
    <Reveal delay={0.2} className="mt-10 flex items-start gap-5 border-t border-white/12 pt-8">
      <span className="label mt-1 shrink-0 text-white/55">Transform</span>
      <p className="display max-w-3xl text-xl leading-snug text-white md:text-2xl" data-testid="transform-statement">{HOME.routes.transform}</p>
    </Reveal>
  </Section>
);

const Differentiation = () => (
  <Section testid="differentiation">
    <SectionHead title={HOME.diff.h2} testid="diff-title" />
    <div className="mt-14 grid gap-10 md:grid-cols-3">
      {HOME.diff.pillars.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.08} className="border-t-2 border-[#0133BA] pt-6 dark:border-primary">
          <span className="label text-muted-foreground">Pillar 0{i + 1}</span>
          <h3 className="mt-4 text-xl font-semibold">{p.title}</h3>
          <p className="mt-4 leading-relaxed text-muted-foreground">{p.text}</p>
        </Reveal>
      ))}
    </div>
  </Section>
);

const AISection = () => (
  <Section tint testid="ai">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <SectionHead eyebrow={HOME.ai.eyebrow} title={HOME.ai.h2} testid="ai-title" />
        <Reveal delay={0.1} className="mt-8 max-w-2xl space-y-5">
          {HOME.ai.body.map((p, i) => <p key={i} className="leading-relaxed text-muted-foreground">{p}</p>)}
        </Reveal>
        <Reveal delay={0.2} className="mt-10"><PrimaryCTA to="/contact" testid="ai-cta">{HOME.ai.cta}</PrimaryCTA></Reveal>
      </div>
      <Reveal delay={0.15} className="flex items-end lg:col-span-5 lg:justify-end">
        <p className="display max-w-sm border-l-2 border-[#0133BA] pl-6 text-2xl leading-snug text-foreground dark:border-primary md:text-3xl" data-testid="ai-memory-line">
          {HOME.ai.memory}
        </p>
      </Reveal>
    </div>
  </Section>
);

const Method = () => (
  <Section id="method" dark testid="method">
    <SectionHead light title={HOME.method.h2} testid="method-title" />
    <RhythmBar className="mt-14" />
    <div className="grid gap-10 pt-8 md:grid-cols-4">
      {HOME.method.steps.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.1}>
          <span className="label text-white/55">0{i + 1}</span>
          <h3 className="mt-4 text-xl font-semibold text-white">{s.title}</h3>
          <p className="mt-3 text-[15px] leading-relaxed text-white/70">{s.text}</p>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Evidence = () => (
  <Section testid="evidence">
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <SectionHead title={HOME.evidence.h2} testid="evidence-title" />
        <Reveal delay={0.1}><p className="mt-6 leading-relaxed text-muted-foreground">{HOME.evidence.body}</p></Reveal>
        <Reveal delay={0.15}><p className="mt-6 text-sm leading-relaxed text-muted-foreground" data-testid="evidence-disclaimer">{HOME.evidence.closing}</p></Reveal>
        <Reveal delay={0.2} className="mt-8"><PrimaryCTA to="/contact" testid="evidence-cta">{HOME.evidence.cta}</PrimaryCTA></Reveal>
      </div>
      <ul className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:col-span-7" data-testid="evidence-list">
        {HOME.evidence.items.map((a, i) => (
          <Reveal key={a} as="li" delay={i * 0.05} className="flex items-start gap-4 bg-background p-6">
            <span className="label mt-1 text-primary">0{i + 1}</span>
            <span className="font-medium leading-snug">{a}</span>
          </Reveal>
        ))}
      </ul>
    </div>
  </Section>
);

const Founder = () => (
  <Section tint testid="founder">
    <div className="grid items-start gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-4">
        <div className="max-w-[320px] overflow-hidden rounded-2xl border border-border lg:max-w-none">
          <img src="/brand/founder.jpg" alt="Arunava Ghosh, Founder of Zenspark Global" className="aspect-[4/5] w-full object-cover object-top" />
        </div>
        <p className="mt-4 text-sm font-semibold">Arunava Ghosh</p>
        <p className="label mt-1 text-muted-foreground">Founder · Zenspark Global</p>
      </Reveal>
      <div className="lg:col-span-8">
        <SectionHead title={HOME.founder.h2} testid="founder-title" />
        <Reveal delay={0.1} className="mt-8 max-w-2xl space-y-5">
          <p className="leading-relaxed text-muted-foreground">{HOME.founder.body}</p>
          <p className="border-l-2 border-[#0133BA] pl-4 text-sm font-medium leading-relaxed dark:border-primary" data-testid="founder-disclaimer">{HOME.founder.disclaimer}</p>
        </Reveal>
        <Reveal delay={0.2} className="mt-8"><TextLink to="/founders-column" testid="founder-cta">{HOME.founder.cta}</TextLink></Reveal>
      </div>
    </div>
  </Section>
);

const Jiya = () => (
  <Section testid="jiya">
    <div className="rounded-2xl border border-dashed border-border p-8 md:p-12">
      <Reveal className="flex flex-wrap items-center gap-3">
        <span className="label inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-muted-foreground" data-testid="jiya-status">
          <span className="h-1.5 w-1.5 rounded-full bg-[#0133BA] dark:bg-primary" /> {HOME.jiya.eyebrow}
        </span>
      </Reveal>
      <div className="mt-8 grid gap-10 lg:grid-cols-12">
        <SectionHead title={HOME.jiya.h2} className="lg:col-span-6" testid="jiya-title" />
        <Reveal delay={0.1} className="lg:col-span-6"><p className="leading-relaxed text-muted-foreground">{HOME.jiya.body}</p></Reveal>
      </div>
    </div>
  </Section>
);

const Closing = () => (
  <Section dark testid="closing" className="overflow-hidden rule-grid-light">
    <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
      <SectionHead light title={HOME.closing.h2} className="lg:col-span-7" testid="closing-title" />
      <Reveal delay={0.1} className="lg:col-span-5">
        <p className="leading-relaxed text-white/70">{HOME.closing.body}</p>
        <div className="mt-8"><PrimaryCTA to="/contact" light testid="closing-cta">{HOME.closing.cta}</PrimaryCTA></div>
      </Reveal>
    </div>
  </Section>
);

export default function Home() {
  useSeo(HOME.seo.title, HOME.seo.description);
  return (
    <div className="p1" data-testid="home-page">
      <Hero />
      <Tension />
      <Fit />
      <TwoRoutes />
      <Differentiation />
      <AISection />
      <Method />
      <Evidence />
      <Founder />
      <Jiya />
      <Closing />
    </div>
  );
}
